import { useEffect, useRef, useState } from "react";
import { LivestreamLayoutCommonProps, ILiveStream } from "../../../types";
import * as Styled from "./Layout4.styled";
import { ArrowLeft, ArrowRight, ChevronRight } from 'react-bootstrap-icons';
import { BigCard } from "../../event-card/big-card";
import { SmallCard } from "../../event-card/small-card";
import { SeeCard } from "../../media-card/see-card";
import LayoutModal from "../../event-card/FullScreenModal";
import { scrollOnce, startContinuousScroll, stopContinuousScroll } from "../../../libs/utils";
import { filterEventsByType } from "../../../libs/utils/eventFilter";
import { EmptyLiveStreams } from "../../empty-state";
import { ToggleFilter } from "../ToggleFilter";


export const Layout4: React.FC<LivestreamLayoutCommonProps> = ({
    events,
    sessionToken,
    isSignedIn,
    activePlans,
    hasFullAccess,
    freeTrialPlanIds,
    onWatchClick
}) => {
    const rightScrollRef = useRef<HTMLDivElement>(null);
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mainEvent, setMainEvent] = useState<ILiveStream | undefined>(undefined);
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const [selectedOption, setSelectedOption] = useState<any>('All');

    useEffect(() => {
        if (events.length && !mainEvent) {
            const firstEvent = events[0];
            setMainEvent(firstEvent);
            setSelectedCardId(firstEvent.id);
        }
    }, [events, mainEvent]);

    const handleSeeAllClick = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const onCardClick = (item: ILiveStream) => {
        setMainEvent(item);
        setSelectedCardId(item.id);
    };

    const filteredEvents = filterEventsByType(events, selectedOption);

    if (!events.length) {
        return <EmptyLiveStreams />;
    }

    if (!mainEvent) return null;

    const remainingEvents = filteredEvents;


    return (
        <>
            <div>
                <Styled.Container>
                    <Styled.MainWrapper>
                        <Styled.LeftSection>
                            <Styled.LeftContent>
                                <Styled.TitleSection>
                                    <Styled.TitleWrapper>
                                        <Styled.Title>Live Streams</Styled.Title>
                                        <Styled.Subtitle>({filteredEvents?.length} Events)</Styled.Subtitle>
                                    </Styled.TitleWrapper>

                                    <Styled.SubtitleActionsWrapper>
                                        <ToggleFilter
                                            selectedOption={selectedOption}
                                            onChange={(option: any) => setSelectedOption(option)}
                                        />
                                        <Styled.SeeAllButton onClick={handleSeeAllClick}>
                                            See all <ChevronRight size={18} />
                                        </Styled.SeeAllButton>
                                    </Styled.SubtitleActionsWrapper>

                                </Styled.TitleSection>

                                {remainingEvents.length > 0 && (
                                    <Styled.ScrollButtonsWrapper>
                                        <Styled.ScrollButton
                                            onClick={() => scrollOnce(rightScrollRef, "left")}
                                            onMouseDown={() => startContinuousScroll(rightScrollRef, "left", scrollIntervalRef)}
                                            onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                                            onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                                            title="Scroll Left"
                                        >
                                            <ArrowLeft size={24} />
                                        </Styled.ScrollButton>
                                        <Styled.ScrollButton
                                            onClick={() => scrollOnce(rightScrollRef, "right")}
                                            onMouseDown={() => startContinuousScroll(rightScrollRef, "right", scrollIntervalRef)}
                                            onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                                            onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                                            title="Scroll Right"
                                        >
                                            <ArrowRight size={24} />
                                        </Styled.ScrollButton>
                                    </Styled.ScrollButtonsWrapper>
                                )}
                            </Styled.LeftContent>
                        </Styled.LeftSection>

                        <Styled.RightSectionWrapper>
                            {
                                filteredEvents.length === 0 ? (
                                    <EmptyLiveStreams showHeader={false} isFiltered={selectedOption !== 'All'} />
                                ) : (
                                    <Styled.RightSection>
                                        <Styled.BigCardWrapper>
                                            <BigCard
                                                event={mainEvent}
                                                isSignedIn={isSignedIn}
                                                hasEligiblePlan={
                                                    isSignedIn &&
                                                    (!!mainEvent.planDetails?.length
                                                        ? !!activePlans.filter((plan) =>
                                                            mainEvent.planDetails?.some((pd) => pd?.id === plan.id)
                                                        ).length ||
                                                        freeTrialPlanIds.includes(mainEvent.planDetails[0]?.id) ||
                                                        hasFullAccess
                                                        : true)
                                                }
                                                onWatchClick={onWatchClick}
                                            />
                                        </Styled.BigCardWrapper>
                                    </Styled.RightSection>
                                )
                            }
                        </Styled.RightSectionWrapper>
                    </Styled.MainWrapper>

                    <div>
                        <div>
                            <Styled.ScrollableList ref={rightScrollRef} scrollable={remainingEvents.length > 4}>
                                {remainingEvents.slice(0, 6).map((item) => (
                                    <Styled.SidebarCardWrapper key={item.id}>
                                        <SmallCard
                                            event={item}
                                            cardClick={() => onCardClick(item)}
                                            isSelected={item.id === selectedCardId}
                                            isSignedIn={isSignedIn}
                                            hasEligiblePlan={
                                                isSignedIn &&
                                                (!!item.planDetails?.length
                                                    ? !!activePlans.filter((plan) =>
                                                        item.planDetails?.some((pd) => pd?.id === plan.id)
                                                    ).length ||
                                                    freeTrialPlanIds.includes(item.planDetails[0]?.id) ||
                                                    hasFullAccess
                                                    : true)
                                            }
                                            onWatchClick={onWatchClick}
                                            bodyWidth="280px"
                                        />
                                    </Styled.SidebarCardWrapper>
                                ))}

                                {remainingEvents.length > 6 && (
                                    <Styled.SidebarCardWrapper onClick={handleSeeAllClick}>
                                        <SeeCard />
                                    </Styled.SidebarCardWrapper>
                                )}
                            </Styled.ScrollableList>
                        </div>
                    </div>
                </Styled.Container>
            </div>

            <LayoutModal
                show={isModalOpen}
                onHide={handleCloseModal}
                filteredLiveEvents={events}
                isSignedIn={isSignedIn}
                activePlans={activePlans}
                hasFullAccess={hasFullAccess}
                freeTrialPlanIds={freeTrialPlanIds}
                onWatchClick={onWatchClick}
            />
        </>
    );
};

